import type {
  HomeDataItem,
  MHome,
  MiniBanners,
  PillButtons,
  Productos,
  SquareBanners,
} from "@/Models/MHome/MHome";
import { useHomeInformation } from "@/Service/Api/Index";
import React, { createContext, useCallback, useContext, useRef } from "react";

type HomeContextType = {
  isLoading: boolean;
  /*************************/
  //listCategoriesTop: Categories[];
  //isLoadingCategories: boolean;
  /***********************/
  homeDataItem: HomeDataItem[];
  productosFiltrados: HomeDataItem[];
  ProductsFilteredHome: HomeDataItem[];
  ViewDataView: (value: number, key: string) => void;
};

const STORAGE_KEY = "homeDataCache";
const CACHE_TTL = 60 * 60 * 1000; // 1 hora en milisegundos

type CacheItem<T> = {
  data: T;
  timestamp: number;
};

export const HomeContext = createContext<HomeContextType | undefined>(
  undefined
);

export const ContextHomeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  //Listado de categoria departamento por defecto.
  /*const { listCategory, isLoading: isLoadingCategories } =
    useListaCategoriaV2();*/

  //Listado del JSON
  const { homeData, isLoading } = useHomeInformation();
  //memoria ref
  const cacheRef = useRef<HomeDataItem[] | null>(null);

  const homeDataItem: HomeDataItem[] =
    homeData
      ?.filter((x) => x?.Contenido)
      .map((r) => ({
        Posicion: r.Posicion,
        IdContenido: r.IdContenido,
      })) || [];

  const pesi: boolean = true;

  const excluidos = ["pillbuttons", "squarebanners", "minibanners"];
  const excluidosHome = ["PillButtons", "SquareBanners", "Carousel3in1"];

  const ProductsFiltered =
    homeDataItem
      ?.filter((x) => x.IdContenido.toLowerCase() === "productos")
      ?.map((r) => r) ?? [];

  const ProductsFilteredHome =
    homeDataItem
      ?.filter((x) => excluidosHome.includes(x.IdContenido))
      ?.map((r) => r)
      .slice(0, 3) ?? [];

  const ProductosOfertas =
    homeDataItem
      ?.filter((x) => !excluidos.includes(x.IdContenido.toLowerCase()))
      ?.map((r) => r) ?? [];

  const productosFiltrados =
    ProductsFiltered.length > 0 ? ProductsFiltered : ProductosOfertas;

  const ViewDataView = useCallback(
    (IdPosition: number, key: string) => {
      switch (key) {
        case "PillButtons":
          return DatapillsButtons(IdPosition);
        case "SquareBanners":
          return DataSquareBanners(IdPosition);
        case "MiniBanners":
          return DataMiniBanners(IdPosition);
        default:
          return DataProductos(IdPosition);
      }
    },
    [homeData, pesi]
  );

  /* Modificación al useMemo 2.0 */
  const DatapillsButtons = useCallback(
    (IdPosition: number): PillButtons[] => {
      //revisamos si esta en memoria.
      const getFromCache = (): MHome[] | null => {
        const storedItem = localStorage.getItem(STORAGE_KEY);
        if (!storedItem) return null;

        try {
          const parsed = JSON.parse(storedItem) as CacheItem<MHome[]>;
          const isExpired = Date.now() - parsed.timestamp > CACHE_TTL;
          if (isExpired || !parsed.data?.length) {
            localStorage.removeItem(STORAGE_KEY);
            return null;
          }
          cacheRef.current = parsed.data;
          return parsed.data;
        } catch (error) {
          console.warn("Error parsing cache:", error);
          localStorage.removeItem(STORAGE_KEY);
          return null;
        }
      };

      const source = getFromCache() ?? homeData;

      const content = source?.find((r) => r.Posicion === IdPosition)
        ?.Contenido as {
        PillButtons?: PillButtons[];
      };
      return content?.PillButtons ?? [];
    },
    [homeData]
  );

  const DataSquareBanners = useCallback(
    (IdPosition: number): SquareBanners[] => {
      const getFromCache = (): MHome[] | null => {
        const storedItem = localStorage.getItem(STORAGE_KEY);
        if (!storedItem) return null;

        try {
          const parsed = JSON.parse(storedItem) as CacheItem<MHome[]>;
          const isExpired = Date.now() - parsed.timestamp > CACHE_TTL;
          if (isExpired || !parsed.data?.length) {
            localStorage.removeItem(STORAGE_KEY);
            return null;
          }
          cacheRef.current = parsed.data;
          return parsed.data;
        } catch (error) {
          console.warn("Error parsing cache:", error);
          localStorage.removeItem(STORAGE_KEY);
          return null;
        }
      };

      const source = getFromCache() ?? homeData;

      return (
        (
          source?.find((r) => r.Posicion == IdPosition)?.Contenido as {
            SquareBanners: SquareBanners[];
          }
        )?.SquareBanners || []
      );
    },
    [homeData]
  );

  const DataMiniBanners = useCallback(
    (IdPosition: number): MiniBanners[] => {
      //revisamos si esta en memoria.
      const getFromCache = (): MHome[] | null => {
        const storedItem = localStorage.getItem(STORAGE_KEY);
        if (!storedItem) return null;

        try {
          const parsed = JSON.parse(storedItem) as CacheItem<MHome[]>;
          const isExpired = Date.now() - parsed.timestamp > CACHE_TTL;
          if (isExpired || !parsed.data?.length) {
            localStorage.removeItem(STORAGE_KEY);
            return null;
          }
          cacheRef.current = parsed.data;
          return parsed.data;
        } catch (error) {
          console.warn("Error parsing cache:", error);
          localStorage.removeItem(STORAGE_KEY);
          return null;
        }
      };

      const source = getFromCache() ?? homeData;

      return (
        (
          source?.find((r) => r.Posicion == IdPosition)?.Contenido as {
            MiniBanners: MiniBanners[];
          }
        )?.MiniBanners || []
      );
    },
    [homeData]
  );

  const DataProductos = useCallback(
    (IdPosition: number): Productos[] => {
      //revisamos si esta en memoria.
      const getFromCache = (): MHome[] | null => {
        const storedItem = localStorage.getItem(STORAGE_KEY);
        if (!storedItem) return null;

        try {
          const parsed = JSON.parse(storedItem) as CacheItem<MHome[]>;
          const isExpired = Date.now() - parsed.timestamp > CACHE_TTL;
          if (isExpired || !parsed.data?.length) {
            localStorage.removeItem(STORAGE_KEY);
            return null;
          }
          cacheRef.current = parsed.data;
          return parsed.data;
        } catch (error) {
          console.warn("Error parsing cache:", error);
          localStorage.removeItem(STORAGE_KEY);
          return null;
        }
      };

      const source = getFromCache() ?? homeData;
      return (
        (
          source?.find((r) => r.Posicion == IdPosition)?.Contenido as {
            Productos: Productos[];
          }
        )?.Productos || []
      );
    },
    [homeData]
  );
  /* *************** */
  /*const listCategoriesTop = useMemo(() => {
    return listCategory || [];
  }, [listCategory]);*/

  return (
    <HomeContext.Provider
      value={{
        isLoading,
        //listCategoriesTop,
        //isLoadingCategories,
        homeDataItem,
        productosFiltrados,
        ProductsFilteredHome,
        ViewDataView,
      }}>
      {children}
    </HomeContext.Provider>
  );
};

export const useContextHomeData = () => {
  const context = useContext(HomeContext);
  if (!context)
    throw new Error(
      "useContextHomeData must be used within a ContextHomeProvider"
    );

  return context;
};
